/**
 * Lo que reclama atención en el CRM (GET /api/atencion): el bloque «Pendiente»
 * del Dashboard y los números del menú lateral.
 *
 * Los dos leen de este almacén y no cada uno por su lado: así el número del
 * menú y la fila del Dashboard son, por construcción, la misma respuesta. El
 * menú decide cuándo se vuelve a pedir (al entrar o salir de una sección, o con
 * el evento `sereno:contadores`); el Dashboard solo lo lee y, con «Actualizar»,
 * pide una fresca.
 */
import { useSyncExternalStore } from 'react';
import { api } from '@/lib/api';
import type { ContadorAtencion } from '@/lib/dispositivos';

export type AreaAtencion = 'dinero' | 'clientes' | 'casas' | 'equipo' | 'sistema';
export type GravedadAtencion = 'urgente' | 'semana' | 'info';

export const AREAS_ATENCION: { id: AreaAtencion; nombre: string }[] = [
  { id: 'dinero', nombre: 'Dinero' },
  { id: 'clientes', nombre: 'Clientes' },
  { id: 'casas', nombre: 'Casas' },
  { id: 'equipo', nombre: 'Equipo' },
  { id: 'sistema', nombre: 'Sistema' },
];

export interface AsuntoAtencion {
  clave: string;
  area: AreaAtencion;
  gravedad: GravedadAtencion;
  titulo: string;
  n: number;
  detalle: string;
  ruta: string;
  importe?: number;
}

export interface ContadoresMenu {
  peticionesNuevas: number;
  candidatosNuevos: number;
  tareasAtencion: number;
  dispositivosAtencion: number;
  conversacionesSinLeer: number;
}

export interface Atencion {
  generadoEn: string;
  asuntos: AsuntoAtencion[];
  alDia: string[];
  sinDatos: string[];
  menu: ContadoresMenu;
  dispositivos: ContadorAtencion | null;
}

interface Estado {
  datos: Atencion | null;
  cargando: boolean;
  error: string | null;
}

let estado: Estado = { datos: null, cargando: false, error: null };
const oyentes = new Set<() => void>();
let enCurso: Promise<void> | null = null;
let otraVez = false;

function cambiar(parcial: Partial<Estado>) {
  estado = { ...estado, ...parcial };
  oyentes.forEach((o) => o());
}

/**
 * Pide la atención. Si ya hay una petición en marcha no lanza otra; si esta
 * pedía datos frescos, se repite al terminar la que había, para no quedarse
 * con números de antes del cambio que provocó el aviso.
 */
export function pedirAtencion(fresco = false): Promise<void> {
  if (enCurso) {
    if (fresco) otraVez = true;
    return enCurso;
  }
  cambiar({ cargando: true });
  enCurso = api
    .getAtencion(fresco)
    .then((datos) => cambiar({ datos, error: null }))
    // Si falla, se queda lo último que se supo: el menú no se vacía por un fallo
    .catch((err: any) => cambiar({ error: err?.message || 'No se pudo cargar' }))
    .finally(() => {
      enCurso = null;
      cambiar({ cargando: false });
      if (otraVez) {
        otraVez = false;
        void pedirAtencion(true);
      }
    });
  return enCurso;
}

function suscribir(oyente: () => void) {
  oyentes.add(oyente);
  return () => oyentes.delete(oyente);
}

export function useAtencion(): Estado {
  return useSyncExternalStore(suscribir, () => estado);
}
